/**
 * Create Actions - Snippet creation with UI integration
 *
 * This action handles snippet creation with full UI integration:
 * - Creates the snippet via the API
 * - Shows success/error flags
 * - Adds the snippet to the editor's quick insert menu
 * - Refreshes the snippets list
 *
 * Separated from fetchActions because it has UI dependencies (flags, intl, editor API)
 * that the pure data operations don't need.
 */
import type { StoreActionApi } from 'react-sweet-state';
import type { IntlShape } from 'react-intl-next';

import type { ADFEntity } from '@atlaskit/adf-utils/types';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type { QuickInsertPlugin } from '@atlaskit/editor-plugin-quick-insert';

import type { WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';

import { createSnippetModalMessages } from '../../modals/CreateSnippetModal.tsx';
import * as BlockServiceClient from '../../services/blockServiceClient.ts';
import { clearSnippetsCache } from '../../services/snippetsCache.ts';
import type { Snippet } from '../../snippetTypes.ts';
import { reportError } from '../../reportError.ts';
import type { SnippetsState } from '../snippetsStore.types.ts';

import {
	addSnippetToQuickInsertAndShare,
	buildCreatePayload,
	getSnippetErrorFlagMessages,
	showSnippetErrorFlag,
	showSnippetSuccessFlag,
} from './actionsHelper.ts';
/**
 * Options for createSnippet that enable UI feedback and editor integration
 */
export type CreateSnippetOptions = {
	/** Flags API for showing success/error notifications */
	flags: WithFlagsProps['flags'];
	/** Intl for message formatting */
	intl: IntlShape;
	/** Editor API for adding snippet to quick insert menu after creation (passed from the caller, e.g. from the modal's props) */
	editorAPI?: PublicPluginAPI<[QuickInsertPlugin]> | null;
	/** When true and site sharing is enabled, share the snippet after creation */
	isShared?: boolean;
	/** Called when share API fails after create (e.g. for analytics) */
	onShareFailure?: (snippetId: string, error: Error) => void;
	/** Called when the create API itself fails; receives the thrown error */
	onSaveFailure?: (error: Error) => void;
};

/**
 * Actions for creating snippets with UI integration.
 */
export const createActions = {
	/**
	 * Creates a new snippet and refreshes the snippets list.
	 *
	 * After successful creation:
	 * - Clears the service cache and fetches the updated list
	 * - Shows a success flag (if options provided)
	 * - Adds the snippet to the quick insert menu
	 *
	 * On error:
	 * - Reports to monitoring
	 * - Shows an error flag (if options provided)
	 * - Keeps existing snippets visible (doesn't reset isLoaded)
	 *
	 * @param name - The snippet name (required)
	 * @param body - The ADF content body
	 * @param description - Optional description text
	 * @param options - UI options including flags, intl, and editor API ref
	 * @returns Promise resolving to the newly created snippet, or null on failure
	 */
	createSnippet:
		(name: string, body: ADFEntity, description?: string, options?: CreateSnippetOptions) =>
		async (storeApi: StoreActionApi<SnippetsState>): Promise<Snippet | null> => {
			const { getState, setState } = storeApi;
			const params = buildCreatePayload(name, body, description);

			setState({ isLoading: true });

			try {
				const snippet = await BlockServiceClient.createSnippet(params);
				const isSnippetSharingForSiteEnabled = getState().isSnippetSharingForSiteEnabled;

				clearSnippetsCache();
				const updatedSnippets = await BlockServiceClient.fetchSnippets({
					isSnippetSharingForSiteEnabled,
				});

				setState({
					snippets: updatedSnippets,
					isLoading: false,
					isLoaded: true,
				});

				if (options) {
					const { flags, intl, editorAPI, isShared, onShareFailure } = options;
					showSnippetSuccessFlag(
						flags,
						intl.formatMessage(createSnippetModalMessages.successFlagTitle, {
							snippetName: snippet.name,
						}),
						intl.formatMessage(createSnippetModalMessages.successFlagDescription),
					);
					await addSnippetToQuickInsertAndShare({
						snippet,
						options: { flags, intl, editorAPI, isShared },
						storeApi,
						onShareFailure,
					});
				}
				return snippet;
			} catch (thrownError) {
				const err = thrownError instanceof Error ? thrownError : new Error(String(thrownError));

				setState({ isLoading: false });
				reportError(err);

				if (options) {
					options.onSaveFailure?.(err);
					const { flagTitle, flagDescription } = getSnippetErrorFlagMessages(
						err,
						createSnippetModalMessages.errorFlagTitle,
						createSnippetModalMessages.errorFlagDescription,
					);
					showSnippetErrorFlag(
						options.flags,
						options.intl.formatMessage(flagTitle),
						options.intl.formatMessage(flagDescription),
					);
					return null;
				}
				throw err;
			}
		},
};
